import React from 'react';
import { faCheckCircle } from '@fortawesome/pro-light-svg-icons/faCheckCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import format from 'date-fns/format';

import TyroHealthDarkLogo from '@medipass/assets/images/Tyro/dark.png';
import { Alert, Button, PageContent, WithErrorMessage, WithLoadingIndicator } from '@medipass/react-core';

import LayoutPage from 'legacy/components/checkout/layout/Page';
import WithAuthentication from 'legacy/components/WithAuthentication';
import { getKeyFromQueryParams } from 'legacy/utils/router';
import { useApproveAndSubmitClaimMutation, useSessionTransactionQuery } from 'queries/transaction';
import { formatPhoneNumber } from 'utils/phone';

import { THO_PRIVACY_POLICY_URL, THO_URL } from '../../constants';

const DHS_LOGO_SRC = '/images/dhs_logo.svg';
const MEDICARE_LOGO_SRC = '/images/medicare_australia_logo.svg';
const DOCUMENT_CONTENT_CLASS_NAME = 'mx-auto w-full max-w-[360px]';
const LINK_CLASS_NAME = 'font-bold text-[#1565d8] underline';
const DISPLAY_LABEL_CLASS_NAME = 'mt-1 mb-2 flex text-xs font-semibold uppercase text-basic-dark';
const DISPLAY_VALUE_CLASS_NAME = 'text-sm leading-[1.25]';

type AdditionalDetail = {
  label: string;
  value?: string;
};

type DisplayServiceItem = {
  additionalDetails: AdditionalDetail[];
  benefitAmount?: string;
  itemKey: string;
  itemTitle: string;
  serviceDate?: string;
};

type AobPractice = {
  address?: string;
  city?: string;
  displayName?: string;
  fullName?: string;
  landline?: string;
  mobile?: string;
  state?: string;
};

type AobPatient = {
  firstName?: string;
  lastName?: string;
  mobile?: string;
};

type AobStaff = {
  firstName?: string;
  lastName?: string;
};

type AobTransactionItem = {
  itemId?: string;
  serviceDate?: string;
  serviceDateString?: string;
};

type AobClaimItem = {
  _id?: string;
  amountExpectedBenefitString?: string;
  displayName?: string;
  itemCode?: string;
  itemId?: string;
  medicare?: {
    accountReferenceNumber?: string;
    numberOfPatientsSeen?: string | number;
    restrictiveOverride?: string;
    scpId?: string;
    selfDeemedCode?: string;
  };
  serviceDate?: string;
  serviceDateString?: string;
};

type AobClaim = {
  _id?: string;
  claimItems?: AobClaimItem[];
  healthFundAccount?: {
    cardRank?: string | number | null;
    membershipNumber?: string;
  };
  medicare?: {
    assignmentOfBenefit?: {
      agreementDate?: string;
      agreementType?: string;
      isAssignorPatient?: boolean;
      status?: string;
    };
    benefitPayee?: {
      providerFullName?: string;
      providerNumber?: string;
    };
    lspNumber?: string;
    referral?: {
      issueDateString?: string;
      periodType?: string;
      providerFullName?: string;
      providerNumber?: string;
    };
  };
  professionalCategory?: {
    displayName?: string;
  };
  providerNumber?: string;
  status?: string;
};

type AobTransaction = {
  _id?: string;
  claims?: AobClaim[];
  created?: string;
  createdString?: string;
  items?: AobTransactionItem[];
  patient?: AobPatient;
  practice?: AobPractice;
  professionalCategory?: {
    displayName?: string;
  };
  staff?: AobStaff;
};

function hasDisplayValue(value: unknown): value is string | number {
  if (typeof value === 'string') {
    return value.trim() !== '';
  }

  return typeof value === 'number';
}

function getFirstDisplayValue(...values: unknown[]) {
  return values.find(hasDisplayValue);
}

function toDisplayString(value: unknown) {
  return hasDisplayValue(value) ? String(value) : undefined;
}

function formatDateDisplayValue(value: unknown) {
  if (!hasDisplayValue(value)) {
    return undefined;
  }

  const stringValue = String(value);

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(stringValue)) {
    return stringValue;
  }

  const isoDateMatch = stringValue.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (isoDateMatch) {
    return `${isoDateMatch[3]}/${isoDateMatch[2]}/${isoDateMatch[1]}`;
  }

  try {
    return format(stringValue, 'DD/MM/YYYY');
  } catch (error) {
    return stringValue;
  }
}

function formatDateTimeDisplayValue(value: unknown) {
  if (!hasDisplayValue(value)) {
    return undefined;
  }

  const stringValue = String(value);

  if (/^\d{2}\/\d{2}\/\d{4}( \d{1,2}:\d{2}[AP]M)?$/i.test(stringValue)) {
    return stringValue;
  }

  const isoDateOnlyMatch = stringValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (isoDateOnlyMatch) {
    return `${isoDateOnlyMatch[3]}/${isoDateOnlyMatch[2]}/${isoDateOnlyMatch[1]}`;
  }

  try {
    return format(stringValue, 'DD/MM/YYYY hh:mma');
  } catch (error) {
    return stringValue;
  }
}

function formatName(...parts: unknown[]) {
  return parts.filter(hasDisplayValue).join(' ');
}

function getAddressLines(practice: AobPractice = {}) {
  const locality = [practice.city, practice.state].filter(hasDisplayValue).join(', ');

  return [practice.address, locality].filter(hasDisplayValue);
}

function buildAdditionalDetails({ claim, claimItem }: { claim: AobClaim; claimItem: AobClaimItem }) {
  const itemMedicare = claimItem.medicare || {};
  const details: AdditionalDetail[] = [
    {
      label: 'Referral override code',
      value: toDisplayString(itemMedicare.restrictiveOverride),
    },
    {
      label: 'Equipment ID',
      value: toDisplayString(itemMedicare.accountReferenceNumber),
    },
    {
      label: 'Self deemed code',
      value: toDisplayString(itemMedicare.selfDeemedCode),
    },
    {
      label: 'Number of patients seen',
      value: toDisplayString(itemMedicare.numberOfPatientsSeen),
    },
    {
      label: 'LSPN',
      value: toDisplayString(claim.medicare?.lspNumber),
    },
    {
      label: 'SCP ID',
      value: toDisplayString(itemMedicare.scpId),
    },
  ];

  return details.filter(detail => hasDisplayValue(detail.value));
}

function getTransactionItemForClaimItem(transaction: AobTransaction, claimItem: AobClaimItem) {
  if (!claimItem.itemId) {
    return undefined;
  }

  return transaction.items?.find(item => item.itemId === claimItem.itemId);
}

function buildServiceItems({
  claim,
  transaction,
}: {
  claim: AobClaim;
  transaction: AobTransaction;
}): DisplayServiceItem[] {
  return (claim.claimItems || []).map((claimItem, index) => {
    const transactionItem = getTransactionItemForClaimItem(transaction, claimItem) || {};
    const itemTitle =
      [claimItem.itemCode, claimItem.displayName].filter(Boolean).join(' - ') || `Service item ${index + 1}`;
    const serviceDate = formatDateDisplayValue(
      getFirstDisplayValue(
        transactionItem.serviceDateString,
        transactionItem.serviceDate,
        claimItem.serviceDateString,
        claimItem.serviceDate,
      ),
    );

    return {
      additionalDetails: buildAdditionalDetails({ claim, claimItem }),
      benefitAmount: claimItem.amountExpectedBenefitString,
      itemKey: [claimItem._id, claimItem.itemId, claimItem.itemCode, index].filter(hasDisplayValue).join('-'),
      itemTitle,
      serviceDate,
    };
  });
}

function getDisplayData(transaction: AobTransaction) {
  const claim = transaction.claims?.[0] || {};
  const assignmentOfBenefit = claim.medicare?.assignmentOfBenefit || {};
  const practice = transaction.practice || {};
  const practiceName = getFirstDisplayValue(practice.displayName, practice.fullName, 'Local Clinic');
  const staff = transaction.staff || {};
  const patient = transaction.patient || {};

  return {
    address: getAddressLines(practice),
    agreementDate: formatDateTimeDisplayValue(
      getFirstDisplayValue(assignmentOfBenefit.agreementDate, transaction.createdString, transaction.created),
    ),
    patientName: formatName(patient.firstName, patient.lastName),
    practiceName: String(practiceName),
    practicePhone: getFirstDisplayValue(practice.landline, practice.mobile),
    providerName: formatName(staff.firstName, staff.lastName),
    providerNumber: claim?.providerNumber,
    providerProfession: getFirstDisplayValue(
      transaction.professionalCategory?.displayName,
      claim.professionalCategory?.displayName,
    ),
    serviceItems: buildServiceItems({ claim, transaction }),
  };
}

function formatBooleanDisplayValue(value?: boolean) {
  if (value === undefined) {
    return undefined;
  }

  return value ? 'Yes' : 'No';
}

function formatAgreementTypeDisplayValue(value?: string) {
  if (value === 'post') {
    return 'Post-assignment';
  }

  if (value === 'pre') {
    return 'Pre-assignment';
  }

  return toDisplayString(value);
}

function ApprovalConfirmation({ transaction }: { transaction: AobTransaction }) {
  const displayData = getDisplayData(transaction);

  React.useEffect(() => {
    document.documentElement.classList.add('aob-approval-confirmation-page');
    document.body.classList.add('aob-approval-confirmation-page');

    return () => {
      document.documentElement.classList.remove('aob-approval-confirmation-page');
      document.body.classList.remove('aob-approval-confirmation-page');
    };
  }, []);

  return (
    <LayoutPage title="Approval received">
      <PageContent breakpoint="mobile">
        <div className="text-center">
          <FontAwesomeIcon aria-hidden="true" className="text-[70px]" icon={faCheckCircle} />
          <div className="mt-4 mb-2 text-xl leading-[1.3]" role="alert">
            Approval received
          </div>
          <div className="text-sm mt-6">{displayData.agreementDate}</div>
        </div>
      </PageContent>
    </LayoutPage>
  );
}

function PoweredByFooter() {
  return (
    <div className="text-center">
      <div className="mb-3 flex items-center justify-center">
        <div className="mr-2 text-sm text-[#747474]">Powered by</div>
        <img src={TyroHealthDarkLogo} alt="Tyro Health" className="h-auto w-[146px]" />
      </div>
      <div className="text-sm">
        <a className={LINK_CLASS_NAME} href={THO_URL} rel="noreferrer noopener" target="_blank">
          Learn more
        </a>{' '}
        |{' '}
        <a className={LINK_CLASS_NAME} href={THO_PRIVACY_POLICY_URL} rel="noreferrer noopener" target="_blank">
          Privacy policy
        </a>
      </div>
    </div>
  );
}

function InformationBanner() {
  return (
    <Alert
      palette="info"
      variant="tint"
      className={`${DOCUMENT_CONTENT_CLASS_NAME} border-[#155db8] bg-[#eef5ff] text-[#2f2f2f] [&_svg]:text-[#155db8]`}
    >
      <p className="mb-4">
        Please review the following information and click the <strong>Approve</strong> button below to assign the
        benefit to the health professional.
      </p>
      <p>Otherwise, please contact the practice directly.</p>
    </Alert>
  );
}

function DetailBlock({ children, label }: { children: React.ReactNode; label: string }) {
  if (!children) {
    return null;
  }

  return (
    <div>
      <div className={DISPLAY_LABEL_CLASS_NAME}>{label}</div>
      <div className={DISPLAY_VALUE_CLASS_NAME}>{children}</div>
    </div>
  );
}

function AgreementHeader({ displayData }: { displayData: ReturnType<typeof getDisplayData> }) {
  return (
    <div className={DOCUMENT_CONTENT_CLASS_NAME}>
      <div className="mb-7 text-center">
        <img
          src={DHS_LOGO_SRC}
          alt="Australian Government Services Australia"
          className="mx-auto mb-2 h-auto w-[112px]"
        />
        <img src={MEDICARE_LOGO_SRC} alt="Medicare" className="mx-auto h-auto w-[112px]" />
        <div className="mt-6 text-lg leading-[1.25]">Bulk Bill Assignment Advice</div>
      </div>

      <div className="grid grid-cols-2 gap-x-[22px] gap-y-6 max-[360px]:grid-cols-1">
        <DetailBlock label="Location">
          <>
            <div className="font-bold mb-1">{displayData.practiceName}</div>
            <div className="space-y-0.5">
              {displayData.address.map(line => (
                <div key={line}>{line}</div>
              ))}
              {displayData.practicePhone && (
                <a className="text-primary-base underline" href={`tel:${displayData.practicePhone}`}>
                  {formatPhoneNumber(displayData.practicePhone)}
                </a>
              )}
            </div>
          </>
        </DetailBlock>

        <DetailBlock label="Service Provider">
          <>
            <div className="font-bold mb-1">{displayData.providerName}</div>
            <div className="space-y-0.5">
              {displayData.providerNumber && <div>{displayData.providerNumber}</div>}
              {displayData.providerProfession && <div>{displayData.providerProfession}</div>}
            </div>
          </>
        </DetailBlock>

        <DetailBlock label="Patient">
          <>
            <div className="font-bold mb-1">{displayData.patientName}</div>
          </>
        </DetailBlock>
      </div>
    </div>
  );
}

function ServiceSummary({ serviceItem }: { serviceItem: DisplayServiceItem }) {
  const [isShowingAdditionalDetails, setIsShowingAdditionalDetails] = React.useState(false);
  const hasAdditionalDetails = serviceItem.additionalDetails.length > 0;

  return (
    <div className={`${DOCUMENT_CONTENT_CLASS_NAME} bg-[#f7f7fb]`}>
      <div className="text-[13px] leading-[1.25] font-bold">{serviceItem.itemTitle}</div>
      <div className="mt-3.5 flex justify-between gap-5 py-[17px] text-sm leading-[1.3] uppercase">
        <div>Date of service</div>
        <div className="text-right">{serviceItem.serviceDate}</div>
      </div>
      <div className="flex justify-between gap-5 border-t border-[#dedfe5] py-[17px] text-sm leading-[1.3] uppercase">
        <div>Expected MBS benefit</div>
        <div className="text-right">{serviceItem.benefitAmount}</div>
      </div>
      {hasAdditionalDetails && (
        <div>
          <button
            aria-expanded={isShowingAdditionalDetails}
            className="flex w-full items-center justify-center gap-2 border-t border-[#dedfe5] pt-6 pb-0 text-center text-sm leading-none font-bold text-secondary-base uppercase"
            onClick={() => setIsShowingAdditionalDetails(currentValue => !currentValue)}
            type="button"
          >
            {isShowingAdditionalDetails ? 'Hide additional details' : 'Show additional details'}
            <span
              aria-hidden="true"
              className={`inline-block h-2 w-2 border-r-2 border-b-2 border-secondary-base ${
                isShowingAdditionalDetails ? 'rotate-[225deg] translate-y-0.5' : 'rotate-45 -translate-y-0.5'
              }`}
            />
          </button>

          {isShowingAdditionalDetails && (
            <div className="mt-6 border-t border-[#dedfe5]">
              {serviceItem.additionalDetails.map(detail => (
                <div
                  className="flex justify-between gap-5 border-b border-[#dedfe5] py-[15px] text-sm leading-[1.3] uppercase last:border-b-0"
                  key={detail.label}
                >
                  <div>{detail.label}</div>
                  <div className="text-right">{detail.value}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AssignmentOfBenefitDetails({ transaction }: { transaction: AobTransaction }) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const assignmentOfBenefit = transaction.claims?.[0]?.medicare?.assignmentOfBenefit || {};
  const details = [
    {
      label: 'Is the assignor the patient',
      value: formatBooleanDisplayValue(assignmentOfBenefit.isAssignorPatient),
    },
    {
      label: 'Assignment type',
      value: formatAgreementTypeDisplayValue(assignmentOfBenefit.agreementType),
    },
  ].filter(detail => hasDisplayValue(detail.value));

  if (details.length === 0) {
    return null;
  }

  return (
    <>
      <PageContent breakpoint="mobile" className="py-6" wrapperProps={{ className: 'bg-white [overflow-anchor:none]' }}>
        <button
          aria-expanded={isExpanded}
          className="flex w-full items-center justify-center gap-3 py-2 text-center text-sm leading-none font-bold text-secondary-base uppercase"
          onClick={() => setIsExpanded(currentValue => !currentValue)}
          type="button"
        >
          Assignment of benefit details
          <span
            aria-hidden="true"
            className={`inline-block h-2 w-2 border-r-2 border-b-2 border-secondary-base ${
              isExpanded ? 'rotate-[225deg] translate-y-0.5' : 'rotate-45 -translate-y-0.5'
            }`}
          />
        </button>
      </PageContent>

      {isExpanded && (
        <PageContent
          breakpoint="mobile"
          className="py-6"
          wrapperProps={{ className: 'border-t border-[#dedfe5] bg-[#f7f7fb] [overflow-anchor:none]' }}
        >
          <div className={`${DOCUMENT_CONTENT_CLASS_NAME} bg-[#f7f7fb] px-6`}>
            {details.map(detail => (
              <div
                className="flex justify-between gap-5 border-b border-[#dedfe5] py-[17px] text-sm leading-[1.3] uppercase last:border-b-0"
                key={detail.label}
              >
                <div>{detail.label}</div>
                <div className="text-right">{detail.value}</div>
              </div>
            ))}
          </div>
        </PageContent>
      )}
    </>
  );
}

function AgreementDocument({ transaction }: { transaction: AobTransaction }) {
  const displayData = getDisplayData(transaction);
  const claimId = transaction.claims?.[0]?._id;
  const { approveAndSubmitClaim, approveAndSubmitClaimMutation } = useApproveAndSubmitClaimMutation({
    claimId,
    transactionId: transaction._id,
  });
  const assignmentOfBenefitStatus = transaction.claims?.[0]?.medicare?.assignmentOfBenefit?.status;
  const claimStatus = transaction.claims?.[0]?.status;
  const isApproved =
    approveAndSubmitClaimMutation.isSuccess || assignmentOfBenefitStatus === 'approved' || claimStatus === 'submitted';

  React.useEffect(() => {
    if (isApproved) {
      document.documentElement.classList.remove('aob-agreement-page');
      document.body.classList.remove('aob-agreement-page');
      return undefined;
    }

    document.documentElement.classList.add('aob-agreement-page');
    document.body.classList.add('aob-agreement-page');

    return () => {
      document.documentElement.classList.remove('aob-agreement-page');
      document.body.classList.remove('aob-agreement-page');
    };
  }, [isApproved]);

  if (isApproved) {
    return <ApprovalConfirmation transaction={approveAndSubmitClaimMutation.data || transaction} />;
  }

  return (
    <LayoutPage title="Medicare Bulk Bill Assignment Advice">
      <PageContent breakpoint="mobile" wrapperProps={{ className: 'bg-white' }}>
        <InformationBanner />
      </PageContent>

      <PageContent breakpoint="mobile" className="pt-0" wrapperProps={{ className: 'bg-white' }}>
        <AgreementHeader displayData={displayData} />
      </PageContent>

      <PageContent
        breakpoint="mobile"
        wrapperProps={{
          className: 'border-y border-[#d9dce5] bg-[#f7f7fb]',
        }}
      >
        <div className="space-y-6">
          {displayData.serviceItems.map(serviceItem => (
            <ServiceSummary key={serviceItem.itemKey} serviceItem={serviceItem} />
          ))}
        </div>
      </PageContent>

      <PageContent breakpoint="mobile" className="pb-0" wrapperProps={{ className: 'bg-white' }}>
        <div className={DOCUMENT_CONTENT_CLASS_NAME}>
          <div className="mb-5 text-sm leading-[1.35] font-bold">
            By clicking the Approve button, you assign the benefit to the health professional for service(s) rendered.
          </div>

          <Button
            className="mb-6 min-h-[42px] w-full rounded-none border-0 bg-[#e8007f] text-lg leading-none font-extrabold tracking-[0.02em] text-white uppercase hover:bg-[#e8007f] focus:bg-[#e8007f] disabled:cursor-default disabled:opacity-70"
            disabled={approveAndSubmitClaimMutation.isPending || isApproved || !transaction._id || !claimId}
            loading={approveAndSubmitClaimMutation.isPending}
            onClick={approveAndSubmitClaim}
            palette="secondary"
            type="button"
            variant="filled"
          >
            Approve
          </Button>

          {approveAndSubmitClaimMutation.isError && (
            <div className="-mt-2 mb-5 leading-[1.35] text-[#b00020]" role="alert">
              We could not approve this agreement. Please try again.
            </div>
          )}

          <div className="text-xs leading-[1.35]">
            On assessment of your claim, Services Australia may adjust the benefit payable in accordance with the rules
            set out in the Medicare Benefits Schedule.
          </div>
        </div>
      </PageContent>

      <AssignmentOfBenefitDetails transaction={transaction} />

      <PageContent
        breakpoint="mobile"
        wrapperProps={{
          className: 'border-t border-[#dedfe5] bg-[#f7f7fb] [overflow-anchor:none]',
        }}
      >
        <PoweredByFooter />
      </PageContent>
    </LayoutPage>
  );
}

function MedicareBulkBillAgreementPageContent({ transactionId }: { transactionId?: string | null }) {
  const { transactionQuery } = useSessionTransactionQuery({ transactionId });

  const isLoading = transactionQuery.isPending;
  const isError = transactionQuery.isError;

  return (
    <WithLoadingIndicator isActive={isLoading}>
      <WithErrorMessage
        isActive={isError}
        description="Unable to load this Medicare Bulk bill benefit agreement."
        error={transactionQuery.error || undefined}
      >
        {transactionQuery.isSuccess && transactionQuery.data && (
          <AgreementDocument transaction={transactionQuery.data} />
        )}
      </WithErrorMessage>
    </WithLoadingIndicator>
  );
}

export default function MedicareBulkBillAgreementPage() {
  const transactionId = getKeyFromQueryParams('transactionId');

  return (
    <WithAuthentication isSelfCheckoutSession requiresValidAccountToken={false} requiresValidSessionToken>
      <MedicareBulkBillAgreementPageContent transactionId={transactionId} />
    </WithAuthentication>
  );
}
